import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import CircledIcon from '../../../components/CircledIcon';
import { Container, Row, Col } from 'react-bootstrap';
import { useState } from 'react';

export type SiteOptionsType = {
  title: string;
  subTitles: string[];
}[];

const SiteOptions: SiteOptionsType = [
  {
    title: 'Privacy Questions',
    subTitles: [
      'How is my confidential information protected on this Web site?',
      'What is your privacy policy? Will my personal information ever be shared with a third party?',
      'Are there any additional steps I should take to further protect my financial and personal information?',
      'Are cookies used by this site?',
    ],
  },

  {
    title: 'Technical Questions',
    subTitles: [
      'What are the system/minimum browser requirements to use this site? How do I download a different browser?',
      'What are temporary Internet files and how can I delete them and clear the cache?',
    ],
  },

  {
    title: 'General Questions',
    subTitles: [
      'How do I register for the "Forgot Your PIN/Passcode?" feature?',
      'How do I get my PIN/Passcode if I have forgotten it?',
      'How do I update my address?',
      'How do I update my life insurance beneficiaries?',
      'Is using the online contact form (Contact Us) the only way to submit a question?',
    ],
  },
];

export type FAQsSiteModalProps = {
  showFAQsSiteModal: boolean;
  handleCloseFAQsSiteModal: () => void;
};

function FAQsSiteModal({
  showFAQsSiteModal,
  handleCloseFAQsSiteModal,
}: FAQsSiteModalProps) {
  const [privacyQuestionsShown, setShowPrivacyQuestions] = useState(false);
  const [technicalQuestionsShown, setShowTechnicalQuestions] = useState(false);
  const [generalQuestionsShown, setShowGeneralQuestions] = useState(false);

  const handleShowQuestions = (idx: number, title: string) => {
    if (idx === 0 && title === 'Privacy Questions') {
      setShowPrivacyQuestions((prev) => !prev);
    } else if (idx === 1 && title === 'Technical Questions') {
      setShowTechnicalQuestions((prev) => !prev);
    } else {
      setShowGeneralQuestions((prev) => !prev);
    }
  };

  const handleShowLinks = (parentIdx: number, idx: number, option: string) => {
    if (privacyQuestionsShown && parentIdx === 0) {
      return (
        <div className={'d-flex mb-0 pt-6 text-fs-6 fw-light'} key={idx}>
          <span className={'d-flex flex-column lh-xs fw-light text-fs-6'}>
            The security and confidentiality of your personal information is a
            matter we take very seriously. We protect your information by:
            <ul>
              <li>
                <span className={'lh-xs fw-light text-fs-6'}>
                  encrypting the information when you submit it to us.
                  Encryption is the process used to scramble confidential data
                  so that it can be passed safely between two points without
                  being read by others.
                </span>
              </li>
              <li>
                <span className={'lh-xs fw-light text-fs-6'}>
                  storing your information in a secure environment that uses
                  firewalls and other security measures to prevent unauthorized
                  access to this information.
                </span>
              </li>
            </ul>
            <span className={'lh-xs fw-light text-fs-6'}>
              In order to provide additional security protection, your browser
              will never cache pages that display personal information on this
              site. This insures that the "Back" button cannot be used to view
              previously displayed pages. To navigate through the site, please
              use the buttons, links and menus supplied directly on the pages.
            </span>
          </span>
        </div>
      );
    }

    if (technicalQuestionsShown && parentIdx === 1) {
      return (
        <div
          className={'d-flex flex-column mb-0 pt-6 text-fs-6 fw-light'}
          key={idx}
        >
          <ul>
            <li>
              <span className={'lh-xs fw-light text-fs-6'}>
                You will need to enable pop-ups to ensure the Interlink Service
                Center works correctly.
              </span>
            </li>
            <li>
              <span className={'lh-xs fw-light text-fs-6'}>
                Browser Requirements: Internet Explorer 11+, Firefox 3.x, Chrome
                10.x, Opera 11.x or Safari 5.x.
              </span>
            </li>
            <li>
              <span className={'lh-xs fw-light text-fs-6'}>
                Mobile Devices: Supported mobile devices: many mobile devices
                are supported on Android 4 and iOS 5-7.
              </span>
            </li>
            <li>
              <span className={'lh-xs fw-light text-fs-6'}>
                Best viewed at a screen resolution of 1024x768 pixels.
              </span>
            </li>
          </ul>
        </div>
      );
    }

    if (generalQuestionsShown && parentIdx === 2) {
      return (
        <div className={'d-flex mb-0 pt-6 text-fs-6 fw-light'} key={idx}>
          <span className={'lh-xs fw-light text-fs-6'}>
            Register for the &quot;Forgot Your Password?&quot; feature by going
            to the Login &amp; Recovery on the Profile page. Once this feature
            is activated, should you ever forget your Password, you will be able
            to securely log into this site by successfully answering the
            specific questions you selected when you registered.
          </span>
        </div>
      );
    }
  };

  return (
    <>
      <Modal
        show={showFAQsSiteModal}
        onHide={handleCloseFAQsSiteModal}
        dialogClassName="modal-xl"
        aria-labelledby="example-custom-modal-styling-title"
      >
        <Modal.Header closeButton className={'modal-header no-border'}>
          <Modal.Title className={'text-sm-20-lg-24'}>
            <span className={'d-none d-lg-inline-flex me-6'}>
              <CircledIcon
                icon={light('comments-question-check')}
                variant={'icon-emphasized'}
              />
            </span>
            Site
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container className={'p-0'}>
            {SiteOptions.map(({ title, subTitles }, idx) => {
              return (
                <Row key={idx} className={'mb-13'}>
                  <Col>
                    <h6
                      className={
                        'mb-0 pb-4 border-bottom border-info text-primary text-fs-base fw-light'
                      }
                    >
                      {title}
                    </h6>
                  </Col>
                  <Col className={'col-12 '}>
                    {subTitles.map((option, index) => {
                      return index === 0 ? (
                        <div
                          onClick={() => handleShowQuestions(idx, title)}
                          className={
                            'd-flex flex-column cursor-pointer d-flex mb-0 py-6 text-fs-6 border-bottom border-info fw-light'
                          }
                          key={index}
                        >
                          {option}
                          {handleShowLinks(idx, index, option)}
                        </div>
                      ) : (
                        <div
                          className={
                            'd-flex mb-0 py-6 text-fs-6 border-bottom border-info fw-light'
                          }
                          key={idx}
                        >
                          {option}
                        </div>
                      );
                    })}
                  </Col>
                </Row>
              );
            })}
          </Container>
        </Modal.Body>
        <Modal.Footer className={'pt-0'}>
          <Button
            variant="primary-outline"
            size="lg"
            onMouseDown={handleCloseFAQsSiteModal}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default FAQsSiteModal;
